import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Pills, Empty } from "barte-design-system";
import { ArrowRightIcon } from "@phosphor-icons/react";
import { usePostHog } from "@posthog/react";

import { Accordion, Skeleton } from "@shared/components";
import { formatDateToBr, formatTimeToBr } from "@shared/helpers";
import {
  useGetRatePlanHistoryDetail,
  useGetRatePlansDetails,
} from "@payfac/services";
import { TRACK_EVENTS } from "@payfac/constants";

import { getRatePlanTaxDiff } from "./helpers";
import { METHOD_ACCORDION_CONFIG } from "./modal-history-detail.constants";
import {
  InstallmentsTable,
  MethodAccordion,
  MethodAccordionSkeleton,
} from "./components";

import styles from "./modal-history-detail.module.css";

import type { ModalHistoryDetailProps } from "./modal-history-detail.type";

export const ModalHistoryDetail = ({
  isOpen,
  planId,
  historyId,
  rowFallback,
  onClose,
}: ModalHistoryDetailProps) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const [activeItems, setActiveItems] = useState<string[]>([]);

  const { data: response, isLoading } = useGetRatePlanHistoryDetail({
    planId: Number(planId),
    historyId,
  });

  const { data: planResponse } = useGetRatePlansDetails({
    planId: Number(planId),
  });

  const detail = response?.data;
  const taxAggregateVersion = (
    String(planResponse?.data?.taxAggregateVersion ?? "2") as "1" | "2"
  );

  const diff = useMemo(() => {
    if (!detail?.tax) return null;
    return getRatePlanTaxDiff(detail.tax, detail.lastHistory?.tax);
  }, [detail]);

  useEffect(() => {
    if (!isOpen) {
      setActiveItems([]);
      return;
    }

    if (historyId) {
      posthog.capture(TRACK_EVENTS.RATE_PLAN.HISTORY_DETAIL_VIEWED, {
        plan_id: planId,
        history_id: historyId,
      });
    }
  }, [isOpen, historyId]);

  const planName = planResponse?.data?.name;

  const formatDateTime = (value?: string | null) =>
    `${formatDateToBr(value)} ${formatTimeToBr(value)}`;

  const newStartedAt = detail?.startedAt || rowFallback?.startedAt;
  const oldStartedAt = detail?.lastHistory?.startedAt;
  const author = detail?.createdBy || rowFallback?.createdBy;
  const hasAnyDate = Boolean(newStartedAt || oldStartedAt);

  const headerTitle = (
    isLoading && !hasAnyDate ? (
      <div className={styles.header}>
        <Skeleton height="1rem" width="9rem" />
        <Skeleton height="0.875rem" width="11rem" />
      </div>
    ) : (
      <div className={styles.header}>
        {!hasAnyDate && (
          <span className={styles["header-date-new"]}>
            {planName ?? t("payfac.pageRatePlanHistoryModalTitleFallback")}
          </span>
        )}

        {oldStartedAt && (
          <>
            <span className={styles["header-date-old"]}>
              {formatDateTime(oldStartedAt)}
            </span>
            <span className={styles["header-arrow"]}>
              <ArrowRightIcon size={14} weight="bold" />
            </span>
          </>
        )}

        {newStartedAt && (
          <span className={styles["header-date-new"]}>
            {formatDateTime(newStartedAt)}
          </span>
        )}

        {diff && diff.totalChanges > 0 && (
          <Pills
            label={t("payfac.pageRatePlanHistoryModalChangeCountPlural", {
              count: diff.totalChanges,
            })}
            variant="light"
            state="default"
            size="sm"
          />
        )}

        {author && (
          <>
            <span className={styles["header-divider"]}>·</span>
            <span className={styles["header-author"]}>{author}</span>
          </>
        )}
      </div>
    )
  ) as unknown as string;

  return (
    <Modal
      className={styles.modal}
      isOpen={isOpen}
      onClose={onClose}
      title={headerTitle}
      size="sm"
      footer={{
        primaryButton: {
          children: t("payfac.pageRatePlanHistoryModalCloseButton"),
          onClick: onClose,
        },
        buttonLayout: "hug",
      }}
    >
      <div className={styles.content}>
        {isLoading && <MethodAccordionSkeleton />}

        {!isLoading && diff && diff.totalChanges > 0 && (
          <Accordion.Root
            activeItems={activeItems}
            onHandleActiveItems={setActiveItems}
            className={styles["accordion-root"]}
          >
            {METHOD_ACCORDION_CONFIG.map((config) => {
              const isCredit = config.diffKey === "credit";
              const section = isCredit ? diff.credit : diff[config.diffKey];
              const count = isCredit
                ? diff.credit.totalChanges
                : section.changes.length;

              const extraContent = isCredit ? (
                <InstallmentsTable
                  installments={diff.credit.installmentChanges}
                  taxAggregateVersion={taxAggregateVersion}
                />
              ) : undefined;

              return (
                <MethodAccordion
                  key={config.id}
                  id={config.id}
                  icon={config.icon}
                  title={t(config.titleKey)}
                  changes={section.changes}
                  count={count}
                  extraContent={extraContent}
                />
              );
            })}
          </Accordion.Root>
        )}

        {!isLoading && (!diff || diff.totalChanges === 0) && (
          <div className={styles.empty}>
            <Empty
              type="empty"
              description={
                detail && !detail.lastHistory
                  ? t("payfac.pageRatePlanHistoryModalEmptyFirstVersion")
                  : t("payfac.pageRatePlanHistoryModalEmpty")
              }
              showButton={false}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
